import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/api";

const STORAGE_KEY = "nour_location";

function read() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch { return null; }
}

function persist(loc) {
  if (loc) localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
}

// Source priority : manual > geolocation > cached > none
export function useLocation() {
  const [location, setLocation] = useState(read);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);

  const setManual = useCallback((loc) => {
    const data = { ...loc, source: "manual", at: Date.now() };
    setLocation(data);
    persist(data);
    setError(null);
  }, []);

  const detect = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Géolocalisation non supportée par ce navigateur.");
      return Promise.reject(new Error("no_geo"));
    }
    setRequesting(true);
    setError(null);
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          // Reverse-geocode for label
          let label = `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
          try {
            const rg = await axios.get(`${API}/reverse-geocode`, { params: { lat, lon }, timeout: 8000 });
            if (rg.data?.label) label = rg.data.label;
          } catch (_) {}
          const loc = { lat, lon, label, source: "geo", at: Date.now() };
          setLocation(loc);
          persist(loc);
          setRequesting(false);
          resolve(loc);
        },
        (err) => {
          setRequesting(false);
          if (err.code === 1) setError("Permission de géolocalisation refusée. Saisissez une ville manuellement.");
          else if (err.code === 2) setError("Position indisponible.");
          else if (err.code === 3) setError("Délai dépassé.");
          else setError("Impossible de récupérer la position.");
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }, []);

  // Auto-detect on first use if nothing cached
  useEffect(() => {
    if (!location) {
      detect().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { location, setLocation: setManual, detect, requesting, error };
}
